import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Suspense, useMemo, useRef, useState } from 'react'

import { ProgressDessert } from 'components/members/components/ProgressDessert/ProgressDessert'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { cn } from 'utils'
import { getEmployersTodos, getGetEmployersTodosQueryKey, usePatchEmployersTodosId } from 'utils/web_api'
import type { EmployersTodo, EmployersTodoCategory, GetEmployersTodosCategoriesItem } from 'utils/web_api.schemas'

import { TodoItem } from './TodoItem'

const HIDE_GROUP_AFTER_N_HOURS_SINCE_LAST_COMPLETION = 72 // 3 days
const ALL_CATEGORIES: EmployersTodoCategory[] = ['onboarding', 'general']

function shouldHideGroup (todos: EmployersTodo[]): boolean {
  const allCompleted = todos.every(todo => todo.status === 'completed')
  if (!allCompleted) return false
  const mostRecentCompletedAt = todos.reduce((latest, todo) => {
    if (new Date(todo.completed_at!).getTime() > latest) {
      return new Date(todo.completed_at!).getTime()
    }
    return latest
  }, 0)
  return mostRecentCompletedAt < Date.now() - HIDE_GROUP_AFTER_N_HOURS_SINCE_LAST_COMPLETION * 60 * 60 * 1000
}

/**
 * Sorts todos so that incomplete pre-requisite todos come before their dependents.
 * Completed todos always filter to the bottom regardless of prerequisite status.
 * Uses a topological sort approach based on pre_requisite_todos relationships.
 */
function sortWithPrerequisites (todos: EmployersTodo[]): EmployersTodo[] {
  const todoMap = new Map(todos.map(t => [t.id, t]))
  const visited = new Set<string>()
  const incompleteResult: EmployersTodo[] = []
  const completedResult: EmployersTodo[] = []

  function visit (todo: EmployersTodo) {
    if (visited.has(todo.id)) return
    visited.add(todo.id)

    // Only visit incomplete prerequisites first (completed ones go to bottom)
    if (todo.pre_requisite_todos) {
      for (const prereq of todo.pre_requisite_todos) {
        const prereqTodo = todoMap.get(prereq.id)
        if (prereqTodo && prereqTodo.status !== 'completed') {
          visit(prereqTodo)
        }
      }
    }

    // Separate completed from incomplete todos
    if (todo.status === 'completed') {
      completedResult.push(todo)
    } else {
      incompleteResult.push(todo)
    }
  }

  for (const todo of todos) {
    visit(todo)
  }

  // Return incomplete todos first, then completed todos at the bottom
  return [...incompleteResult, ...completedResult]
}

function titleForCategory (category: Omit<GetEmployersTodosCategoriesItem, 'all'>, completedAllTasksForCategory: boolean): string {
  if (completedAllTasksForCategory) {
    const title = {
      onboarding: 'You\'ve finished all your onboarding steps!',
      general: 'You have no tasks to complete',
    }
    return title[category as keyof typeof title]
  } else {
    const title = {
      onboarding: 'Your onboarding steps',
      general: 'Other tasks',
    }
    return title[category as keyof typeof title]
  }
}

const ConditionalCottonCandyWrapper = ({ children, enabled }: { children: React.ReactNode, enabled: boolean }) => {
  return (
    <div className={cn('bg-white border rounded-2xl shadow-sm overflow-hidden', enabled ? 'border-emphasis shadow-emphasis' : 'border-color-stroke-light')}>
      {children}
    </div>
  )
}

const TodoListGroup = (
  { todos, category, markTodoCompleted, expandedTodoId, onToggleExpand, markingTodoId }:
  {
    todos: EmployersTodo[],
    category: Omit<GetEmployersTodosCategoriesItem, 'all'>,
    markTodoCompleted: (id: string) => void,
    expandedTodoId: string | null,
    onToggleExpand: (id: string) => void,
    markingTodoId: string | null
  }
) => {
  // Store the initial sort order so it doesn't change when todos are marked complete
  const initialSortOrderRef = useRef<string[] | null>(null)

  // Compute initial sort order only once
  if (initialSortOrderRef.current === null && todos.length > 0) {
    const sorted = [...todos].sort((a, b) => {
      // Sort by completed_at: incomplete first, then completed
      if (!!a.completed_at !== !!b.completed_at) {
        return a.completed_at ? 1 : -1
      }
      // Then sort by closest deadline
      const aDeadline = a.deadline ? new Date(a.deadline).getTime() : Infinity
      const bDeadline = b.deadline ? new Date(b.deadline).getTime() : Infinity
      return aDeadline - bDeadline
    })
    // Apply prerequisite ordering to ensure dependencies come first
    initialSortOrderRef.current = sortWithPrerequisites(sorted).map(t => t.id)
  }

  // Use the stored order to get todos in stable order with latest data
  const sortedTodos = useMemo(() => {
    if (!initialSortOrderRef.current) return todos
    const todoMap = new Map(todos.map(t => [t.id, t]))
    return initialSortOrderRef.current
      .map(id => todoMap.get(id))
      .filter((t): t is EmployersTodo => t !== undefined)
  }, [todos])

  const completedTodos = useMemo(() => (
    todos.filter(todo => todo.status === 'completed')
  ), [todos])

  if (shouldHideGroup(todos)) {
    return <></>
  }

  return (
    <>
      <div className='flex items-center justify-between mb-3 mt-6 px-6'>
        <p className='text-main body '>{titleForCategory(category, completedTodos.length === todos.length)}</p>
        <div className='flex items-center gap-x-2'>
          <p className='text-gray-500 body'>{completedTodos.length}/{todos.length} done</p>
          <ProgressDessert
            completed={completedTodos.length}
            total={todos.length}
            size={20}
            strokeWidth={4}
          />
        </div>
      </div>

      <div className='border-t border-color-stroke-dark'>
        {sortedTodos.map((todo) => (
          <TodoItem
            key={todo.id}
            todo={todo}
            onMarkComplete={() => markTodoCompleted(todo.id)}
            isMarkingComplete={markingTodoId === todo.id}
            isExpanded={expandedTodoId === todo.id}
            onToggleExpand={() => onToggleExpand(todo.id)}
          />
        ))}
      </div>
    </>
  )
}

interface TodoListProps {
  title?: string
  category?: GetEmployersTodosCategoriesItem | GetEmployersTodosCategoriesItem[]
  filter?: (todo: EmployersTodo) => boolean
}

function TodoListComponent ({
  title = 'Your tasks',
  category = ['onboarding', 'general'],
  filter = () => true,
}: TodoListProps) {
  const queryClient = useQueryClient()
  const [expandedTodoId, setExpandedTodoId] = useState<string | null>(null)
  const [markingTodoId, setMarkingTodoId] = useState<string | null>(null)
  const categoriesArray = Array.isArray(category) ? category : [category]

  const { data } = useSuspenseQuery({
    queryKey: getGetEmployersTodosQueryKey({ 'categories[]': categoriesArray }),
    queryFn: () => getEmployersTodos({ 'categories[]': categoriesArray }),
  })

  const markTodoCompleted = usePatchEmployersTodosId({
    mutation: {
      onSuccess: (updatedTodo) => {
        setMarkingTodoId(null)
        queryClient.setQueryData<{ todos: EmployersTodo[] }>(
          getGetEmployersTodosQueryKey({ 'categories[]': categoriesArray }),
          (oldData) => {
            if (!oldData) return oldData
            return {
              ...oldData,
              todos: oldData.todos.map((todo) =>
                todo.id === updatedTodo.id ? updatedTodo : todo
              ),
            }
          }
        )
      },
      onError: () => {
        setMarkingTodoId(null)
      },
    },
  })

  const handleMarkTodoCompleted = (id: string) => {
    setMarkingTodoId(id)
    markTodoCompleted.mutate({ id })
  }

  const handleToggleExpand = (id: string) => {
    setExpandedTodoId(prev => prev === id ? null : id)
  }

  const todos = (data?.todos || []).filter(filter)

  const todosGroupedByCategory = useMemo(() => {
    return todos.reduce((acc, todo) => {
      acc[todo.category] = (acc[todo.category] || []).concat(todo)
      return acc
    }, {} as Partial<Record<typeof EmployersTodoCategory[keyof typeof EmployersTodoCategory], EmployersTodo[]>>)
  }, [todos])

  // Filter to categories that have todos, maintaining the correct order
  const categoriesToRender = ALL_CATEGORIES.filter(cat => todosGroupedByCategory[cat])

  const shouldHideAllGroups = useMemo(() => {
    return categoriesToRender.every(cat => {
      const catTodos = todosGroupedByCategory[cat]
      return !catTodos || shouldHideGroup(catTodos)
    })
  }, [categoriesToRender, todosGroupedByCategory])

  if (shouldHideAllGroups) {
    return (
      <ConditionalCottonCandyWrapper enabled={false}>
        <div className='p-6'>
          <h2 className='text-main body-bold'>You've finished all your to-dos for now!</h2>
        </div>
      </ConditionalCottonCandyWrapper>
    )
  }

  return (
    <ConditionalCottonCandyWrapper
      enabled={todos.some((todo) => todo.status === 'pending')}
    >
      <h3 className='text-main heading-md pt-6 px-6'>{title}</h3>
      {categoriesToRender.map((cat) => (
        <TodoListGroup
          key={cat}
          todos={todosGroupedByCategory[cat] ?? []}
          category={cat}
          markTodoCompleted={handleMarkTodoCompleted}
          expandedTodoId={expandedTodoId}
          onToggleExpand={handleToggleExpand}
          markingTodoId={markingTodoId}
        />
      ))}
    </ConditionalCottonCandyWrapper>
  )
}

export default function TodoList ({
  title,
  category,
  filter,
}: TodoListProps) {
  const fallback = (
    <ConditionalCottonCandyWrapper enabled={false}>
      <div className='pt-6 px-6'>
        <h3 className='text-main heading-md mb-6'>{title}</h3>
        <div className='flex items-center justify-between mb-4'>
          <SkeletonItem widthClass='w-48' heightClass='h-4' />
          <SkeletonItem widthClass='w-8' heightClass='h-4' />
        </div>
      </div>
      <div className='p-2 rounded-md overflow-hidden'>
        <SkeletonItem widthClass='w-full' heightClass='h-24' />
      </div>
    </ConditionalCottonCandyWrapper>
  )

  return (
    <Suspense fallback={fallback}>
      <TodoListComponent title={title} category={category} filter={filter} />
    </Suspense>
  )
}
